import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, orderBy, query, limit } from "firebase/firestore";
import { getPlanDetails } from "../utils/premiumHelpers";
import firebaseConfig from "../firebaseConfig";

export default function AdminPaymentHistory() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("success"); // success, failed, all
  const [filterPlan, setFilterPlan] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const appId = firebaseConfig.appId;

      // Fetch payment logs - limit to 1000 most recent
      const paymentQuery = query(
        collection(db, `artifacts/${appId}/public/data/subscription_payments`),
        orderBy("createdAt", "desc"),
        limit(1000)
      );

      const querySnapshot = await getDocs(paymentQuery);
      const paymentsList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPayments(paymentsList);
    } catch (err) {
      console.error("Error fetching payments:", err);
      setError("Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredPayments = () => {
    let filtered = payments;

    // Filter by status
    if (filterStatus !== "all") {
      filtered = filtered.filter((p) => p.status === filterStatus);
    }

    // Filter by plan
    if (filterPlan !== "all") {
      filtered = filtered.filter((p) => p.plan === filterPlan);
    }

    // Filter by search (user ID, payment ID, email)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.userId.includes(search) ||
          p.razorpayPaymentId.includes(search) ||
          p.razorpayOrderId.includes(search)
      );
    }

    return filtered;
  };

  const filteredPayments = getFilteredPayments();

  // Calculate statistics
  const successPayments = payments.filter((p) => p.status === "success");
  const totalRevenue = successPayments.reduce((sum, p) => sum + (p.amountPaid || 0), 0);
  const failedCount = payments.filter((p) => p.status === "failed").length;

  if (loading) return <div className="text-center py-8">Loading payment history...</div>;
  if (error) return <div className="text-center py-8 text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-sm text-green-600 dark:text-green-400 font-semibold">Successful Payments</p>
          <p className="text-3xl font-bold text-green-800 dark:text-green-300 mt-2">{successPayments.length}</p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400 font-semibold">Failed Payments</p>
          <p className="text-3xl font-bold text-red-800 dark:text-red-300 mt-2">{failedCount}</p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-600 dark:text-blue-400 font-semibold">Total Revenue</p>
          <p className="text-3xl font-bold text-blue-800 dark:text-blue-300 mt-2">₹{totalRevenue.toLocaleString()}</p>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <p className="text-sm text-purple-600 dark:text-purple-400 font-semibold">Avg Transaction</p>
          <p className="text-3xl font-bold text-purple-800 dark:text-purple-300 mt-2">
            ₹{successPayments.length > 0 ? (totalRevenue / successPayments.length).toFixed(0).toLocaleString() : 0}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-700 p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Search (User ID / Payment ID)
            </label>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
            >
              <option value="all">All</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Plan
            </label>
            <select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
            >
              <option value="all">All Plans</option>
              <option value="1-month">1 Month</option>
              <option value="3-months">3 Months</option>
              <option value="6-months">6 Months</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchPayments}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded transition"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Payment Table */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-600 border-b border-gray-300 dark:border-gray-500">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Date</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">User ID</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Payment ID</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Plan</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Amount</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Duration</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Method</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
            {filteredPayments.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-4 py-4 text-center text-gray-600 dark:text-gray-400">
                  No payments found
                </td>
              </tr>
            ) : (
              filteredPayments.map((payment) => {
                const planDetails = getPlanDetails(payment.plan);
                const createdDate = payment.createdAt?.toDate?.() || new Date(payment.createdAt);

                return (
                  <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-600 transition">
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {createdDate.toLocaleDateString("en-GB")}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono text-xs">
                      {payment.userId.substring(0, 8)}...
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono text-xs">
                      {payment.razorpayPaymentId.substring(0, 12)}...
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {planDetails?.name || payment.plan}
                    </td>
                    <td className="px-4 py-3 font-semibold text-green-600 dark:text-green-400">
                      ₹{payment.amountPaid?.toLocaleString() || 0}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {planDetails?.durationMonths || 0} month(s)
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 capitalize">
                      {payment.paymentMethod || "Unknown"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                          payment.status === "success"
                            ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                            : payment.status === "failed"
                              ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
                              : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300"
                        }`}
                      >
                        {payment.status?.charAt(0).toUpperCase() + payment.status?.slice(1)}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="text-center text-sm text-gray-600 dark:text-gray-400">
        Showing {filteredPayments.length} of {payments.length} payments
      </div>
    </div>
  );
}
